import SecureStorage from "./secureStorage";
import { PARTNER_LINK_TTL_MS } from "./utmDatasCookie";
import { extractUtmParamsFromSearch } from "../components/pages/customRegister/utmCapture";

const ATTRIBUTION_PARAMS = [
  "gclid",
  "wbraid",
  "gbraid",
  "gad_source",
  "bp_id",
] as const;
const FORWARDED_PARAMS = ["plan", "language"] as const;

export const BP_TS_PARAM = "bp_ts";

const BP_TS_PATTERN = /^\d{1,15}$/;

export const readPartnerLinkFromSearch = (
  search: string | URLSearchParams,
  now: number = Date.now()
): { bp_id: string; capturedAt: number } | null => {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  const bp_id = params.get("bp_id");
  if (!bp_id) return null;
  if (!params.has(BP_TS_PARAM)) return { bp_id, capturedAt: now };

  const raw = params.get(BP_TS_PARAM) ?? "";
  const capturedAt = BP_TS_PATTERN.test(raw) ? Number(raw) : Number.NaN;
  if (Number.isSafeInteger(capturedAt) && capturedAt <= now) {
    return now - capturedAt <= PARTNER_LINK_TTL_MS
      ? { bp_id, capturedAt }
      : null;
  }

  const stored = SecureStorage.getAttributionData();
  if (stored?.bp_id === bp_id) {
    const storedAt = stored.bp_id_captured_at ?? stored.captured_at;
    if (typeof storedAt === "number" && storedAt <= now)
      return { bp_id, capturedAt: storedAt };
  }
  return { bp_id, capturedAt: now };
};

export const captureAttributionFromSearch = (search: string): void => {
  if (!search) return;

  const params = new URLSearchParams(search);
  const clickIds: Record<string, string> = {};
  for (const key of ATTRIBUTION_PARAMS) {
    if (key === "bp_id") continue;
    const value = params.get(key);
    if (value) clickIds[key] = value;
  }
  const partnerLink = readPartnerLinkFromSearch(params);
  const utm = extractUtmParamsFromSearch(search);

  if (
    Object.keys(clickIds).length === 0 &&
    !partnerLink &&
    Object.keys(utm).length === 0
  ) {
    return;
  }

  SecureStorage.setAttributionData({
    ...clickIds,
    ...(partnerLink
      ? { bp_id: partnerLink.bp_id, bp_id_captured_at: partnerLink.capturedAt }
      : {}),
    utm,
  });
};

export const buildForwardedSearch = (search: string): string => {
  if (!search) return "";

  const params = new URLSearchParams(search);
  const forwarded = new URLSearchParams();

  for (const key of [...ATTRIBUTION_PARAMS, ...FORWARDED_PARAMS]) {
    const value = params.get(key);
    if (value) forwarded.set(key, value);
  }
  // Without its date, the next page would restart a relayed bp_id's 90 days.
  if (forwarded.has("bp_id") && params.has(BP_TS_PARAM))
    forwarded.set(BP_TS_PARAM, params.get(BP_TS_PARAM) ?? "");
  for (const [key, value] of Object.entries(
    extractUtmParamsFromSearch(search)
  )) {
    if (value) forwarded.set(key, value);
  }

  const result = forwarded.toString();
  return result ? `?${result}` : "";
};
